import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../utils/translations';
import { hasPermission } from '../../utils/permissions';
import FAQSection from './FAQSection';
import GuidesSection from './GuidesSection';
import ForumSection from './ForumSection';
import supabase from '../../lib/supabase';

const { FiHelpCircle, FiLifeBuoy, FiBook, FiMessageSquare } = FiIcons;

const HelpCenterPage = ({ setActiveTab }) => {
  const { user } = useAuth();
  const t = useTranslation(user?.language || 'fr'); // Default to French
  const [activeSection, setActiveSection] = useState('faq');
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const isAdmin = hasPermission(user, 'canEditHelpCenter');

  // Render the appropriate section based on activeSection
  const renderSection = () => {
    switch (activeSection) {
      case 'faq':
        return <FAQSection isAdmin={isAdmin} />;
      case 'guides':
        return <GuidesSection isAdmin={isAdmin} />;
      case 'forum':
        return <ForumSection isAdmin={isAdmin} user={user} />;
      default:
        return <FAQSection isAdmin={isAdmin} />;
    }
  };

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Section Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="max-w-12xl mx-auto px-4 lg:px-6">
          <div className="flex flex-col lg:flex-row lg:space-x-8 overflow-x-auto hide-scrollbar">
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeSection === 'faq'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
              onClick={() => setActiveSection('faq')}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiHelpCircle} className="w-4 h-4" />
                <span>{t('helpCenter.faqTab', 'Frequently Asked Questions')}</span>
              </div>
            </button>

            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeSection === 'guides'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
              onClick={() => setActiveSection('guides')}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiBook} className="w-4 h-4" />
                <span>{t('helpCenter.guidesTab', 'Guides & SOPs')}</span>
              </div>
            </button>

            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeSection === 'forum'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
              onClick={() => setActiveSection('forum')}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiMessageSquare} className="w-4 h-4" />
                <span>{t('helpCenter.forumTab', 'Questions & Answers')}</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-12xl mx-auto">
          {renderSection()}
        </div>
      </div>
    </div>
  );
};

export default HelpCenterPage;